/**
 * Everything the widget learns from its own URL.
 *
 * Three delivery modes have to work from the same code:
 *
 *   book.<domain>/west-bay              a full page
 *   book.<domain>/west-bay?table=12     a QR code on a table tent
 *   an iframe mounted by embed.js       on a partner site
 *
 * The iframe case is why the venue slug is read from the path AND from a query
 * parameter: inside an iframe the path is the widget's own, but the host page
 * passes the venue through the src.
 */

import type { Locale } from '@yp/shared';

const LOCALES: readonly string[] = ['en', 'ar'];

export interface WidgetParams {
  venueSlug: string | null;
  /** Explicit `?lang=`, or null to fall back to the browser and venue default. */
  locale: Locale | null;
  /** From a QR code on a table tent. Carried through for attribution. */
  tableHint: string | null;
  /** Marketing attribution, carried into reservations.source_detail. */
  utm: Record<string, string>;
  partySize: number | null;
  date: string | null;
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  // Meta and Google click ids, which are what actually reconciles a click to a
  // cover when the UTM tags get stripped by a redirect.
  'fbclid',
  'gclid',
] as const;

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALES.includes(value);
}

/**
 * Read the venue slug from the path.
 *
 * `/west-bay` and `/west-bay/` both give `west-bay`. Anything with more than
 * one segment is treated as no slug rather than guessed at - a wrong venue is
 * worse than an explicit "choose a venue".
 */
export function slugFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 1) return null;
  const slug = segments[0]!;
  return /^[a-z0-9-]{2,60}$/i.test(slug) ? slug : null;
}

export function readParams(
  location: { pathname: string; search: string } = window.location,
): WidgetParams {
  const query = new URLSearchParams(location.search);

  const utm: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = query.get(key);
    // Bounded: these end up in a JSONB column and are attacker-controlled.
    if (value) utm[key] = value.slice(0, 120);
  }

  const partySize = Number(query.get('party_size') ?? query.get('guests'));
  const date = query.get('date');

  return {
    venueSlug: query.get('venue') ?? slugFromPath(location.pathname),
    locale: isLocale(query.get('lang')) ? (query.get('lang') as Locale) : null,
    tableHint: query.get('table')?.slice(0, 20) ?? null,
    utm,
    partySize: Number.isFinite(partySize) && partySize >= 1 && partySize <= 50 ? partySize : null,
    date: date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null,
  };
}

/**
 * Locale precedence: URL, then browser, then the venue default.
 *
 * The URL wins because an Arabic QR code on a table tent, or a link inside an
 * Arabic WhatsApp confirmation, must open in Arabic whatever the phone is set
 * to. A guest who reads Arabic and has an English phone is common.
 */
export function resolveLocale(
  fromUrl: Locale | null,
  venueDefault: Locale = 'en',
  languages: readonly string[] = navigator.languages ?? [],
): Locale {
  if (fromUrl) return fromUrl;
  for (const candidate of languages) {
    const base = candidate.split('-')[0];
    if (isLocale(base)) return base;
  }
  return venueDefault;
}

/**
 * What gets recorded against the booking.
 *
 * Flat, string-valued and bounded, because it lands in a JSONB column that a
 * report will later group by.
 */
export function sourceDetail(params: WidgetParams): Record<string, string> {
  return {
    ...params.utm,
    ...(params.tableHint ? { table_hint: params.tableHint } : {}),
    ...(params.locale ? { lang_param: params.locale } : {}),
  };
}
